/**
 * MONSTER TOWN — FISHING. FISHING/BUGS CREW (src/catching/**).
 *
 * Expansion brief §6, and it is deliberately the whole spec: "tap to cast, wait
 * for the bob to dip, tap to catch. That's it — no meter, no tension bar, no
 * losing. A miss just means the fish swims off and comes back."
 *
 * THE STATE MACHINE HAS NO FAILURE STATE IN IT. Read the type: there is no
 * 'lost', no 'escaped', no 'tooSlow'. The four states are
 *
 *      idle -> waiting -> biting -> landing -> waiting (it re-casts itself)
 *                 ^          |
 *                 +----------+   the bite window ran out: SAME fish, again
 *
 * and the only edge that leaves the machine is walking away, going into build
 * mode, or leaving the scene. A three-year-old who taps at the wrong moment
 * gets a plop and a sparkle and the float stays out. A three-year-old who never
 * taps at all gets the bite offered again, for ever.
 *
 * IT RE-CASTS ITSELF ON PURPOSE. Having landed a fish, the float goes straight
 * back out, so fishing is tap-wait-tap-wait-tap from one spot on the bank. The
 * alternative — walk back to the water and find the tap again between every
 * fish — is the kind of friction that ends a three-year-old's interest in a
 * mechanic they have only just understood.
 *
 * WHICH FISH is never decided here: `rollAvailable('fish', engine.rng, 'pond')`
 * asks the availability rules in `src/content/creatures.ts`. Cast at noon and
 * cast at midnight and you are fishing two different ponds. No species name
 * appears in this file.
 */

import * as THREE from 'three'
import { rollAvailable, type CollectionEntry } from '../core/collection'
import { engine } from '../core/engine'
import { bus } from '../core/events'
import { world } from '../core/scene'
import type { SceneId, Vec3 } from '../core/types'
import { app, type GameContext } from '../app/context'
import { instanceCentre } from '../app/interactions'
import { aimLine, aimRod, catchMesh, disposeCatchArt, floatMesh, rodMesh } from './art'
import { isPondWater, pondCentre, pondStand } from './places'
import { claimCatch } from './reward'
import { goThen, keepStill } from './walk'

const TOWN = 'town' as SceneId

/** The id of the water piece a kid can build. Content, matched by string only. */
const WATER_DEF = 'water_pond'

/** How long before a bite. Short enough for a three-year-old's patience. */
const WAIT_MIN_S = 1.4
const WAIT_SPAN_S = 2.8
/** How long the bite is offered. Very generous, and it comes round again. */
const BITE_S = 3.0
/** The leap, then the reward. */
const LAND_S = 0.85
/** Walk further than this from your own float and the line is reeled in. */
const LEASH = 13

type FishState = 'idle' | 'waiting' | 'biting' | 'landing'

interface Rod {
  state: FishState
  /** Where the float is sitting. */
  water: Vec3
  art: THREE.Group
  /**
   * The two moving parts, looked up ONCE. `getObjectByName` walks the subtree,
   * and this render callback runs on every awake frame the rod is out; the
   * older iPad has no p50 headroom to spend on a name search.
   */
  bob: THREE.Object3D | null
  ring: THREE.Object3D | null
  /** The gold burst that pops over the float on a bite. Hidden otherwise. */
  shout: THREE.Object3D | null
  /** The rod in the monster's hands — this crew's prop, not the monster crew's. */
  rod: THREE.Group
  /** Seconds left in the current state. */
  left: number
  /** The fish currently on the hook. Chosen when the bite starts. */
  hooked: CollectionEntry | null
  leap: THREE.Group | null
  t: number
}

let rod: Rod | null = null
let ctxRef: GameContext | null = null
let installed = false
const tmpA = new THREE.Vector3()
const tmpB = new THREE.Vector3()
const tmpTip = new THREE.Vector3()

function townNode(): ReturnType<typeof world.get> {
  return world.get(TOWN)
}

function awake(): boolean {
  const node = townNode()
  return node !== undefined && node.awake && world.activeSceneId === TOWN && !world.transitioning
}

// ---------------------------------------------------------------------------
// casting
// ---------------------------------------------------------------------------

function cast(water: Vec3): void {
  const node = townNode()
  if (node === undefined) return
  reelIn(false)
  const art = floatMesh()
  art.position.set(water.x, 0.06, water.z)
  node.actors.add(art)
  const held = rodMesh()
  node.actors.add(held)
  rod = {
    state: 'waiting',
    water: { x: water.x, y: 0.06, z: water.z },
    art,
    bob: art.getObjectByName('bob') ?? null,
    ring: art.getObjectByName('ring') ?? null,
    shout: art.getObjectByName('shout') ?? null,
    rod: held,
    left: WAIT_MIN_S + engine.rng() * WAIT_SPAN_S,
    hooked: null,
    leap: null,
    t: 0,
  }
  bus.emit('fx:play', { effect: 'splash', at: { ...rod.water }, count: 8 })
  bus.emit('audio:cue', { cue: 'water_splash', at: rod.water })
  bus.emit('monster:action', { action: 'point', seconds: 0.8 })
}

export function reelIn(quiet = true): void {
  if (rod === null) return
  rod.rod.removeFromParent()
  rod.art.removeFromParent()
  // The ripple ring is the ONE piece of geometry this crew builds per cast
  // rather than taking from the shared kit, so it is the one piece that has to
  // be given back. Everything else is cached and must NOT be disposed.
  disposeCatchArt(rod.art)
  rod.leap?.removeFromParent()
  if (!quiet) bus.emit('audio:cue', { cue: 'swish_soft' })
  rod = null
}

// ---------------------------------------------------------------------------
// the bite
// ---------------------------------------------------------------------------

function startBite(r: Rod): void {
  // Widen rather than ever say "nothing is here" — the smiley fish has no time
  // rule at all, so this second call cannot come back empty in the shipped
  // content, and if a future table breaks that we simply keep waiting.
  const entry = rollAvailable('fish', engine.rng, 'pond') ?? rollAvailable('fish', engine.rng)
  if (entry === null) {
    r.left = WAIT_MIN_S + engine.rng() * WAIT_SPAN_S
    return
  }
  r.hooked = entry
  r.state = 'biting'
  r.left = BITE_S
  /**
   * THE BITE HAS TO BE UNMISTAKABLE. Round `vr1`: "the bite itself is
   * unreadable: a small teal blob with a flat 2D white ellipse ring on the
   * water. No dip, no splash, no flash, no exclamation. The most exciting moment
   * fishing has is a hairline and a circle."
   *
   * Three answers, all in the picture: the gold burst comes on over the float
   * (below), the dip now KEEPS THE RED BALL ABOVE THE WATER instead of sinking
   * the whole float out of sight (see the render pass), and the splash is a real
   * splash rather than ten quiet bubbles.
   */
  if (r.shout !== null) r.shout.visible = true
  bus.emit('fx:play', { effect: 'splash', at: { ...r.water }, count: 16 })
  bus.emit('fx:play', { effect: 'bubbles', at: { ...r.water }, count: 10 })
  bus.emit('audio:cue', { cue: 'water_plop', at: r.water })
}

/** The bite window ran out. The fish swims off — and comes straight back. */
function fishSwamOff(r: Rod): void {
  bus.emit('fx:play', { effect: 'splash', at: { ...r.water }, count: 6 })
  bus.emit('audio:cue', { cue: 'water_plop', at: r.water })
  if (r.shout !== null) r.shout.visible = false
  r.hooked = null
  r.state = 'waiting'
  r.left = 0.9 + engine.rng() * 1.6
}

/** A tap while nothing is biting. Not a mistake — a plop, and carry on. */
function plop(r: Rod): void {
  bus.emit('fx:play', { effect: 'splash', at: { ...r.water }, count: 4 })
  bus.emit('audio:cue', { cue: 'water_plop', at: r.water })
  // Being keen is rewarded, gently: an early tap brings the bite forward.
  r.left = Math.min(r.left, 1.1)
}

function land(r: Rod): void {
  const entry = r.hooked
  if (entry === null) return
  const node = townNode()
  r.state = 'landing'
  r.left = LAND_S
  if (r.shout !== null) r.shout.visible = false
  const leap = catchMesh(entry.id)
  leap.position.set(r.water.x, 0.4, r.water.z)
  node?.actors.add(leap)
  r.leap = leap
  bus.emit('fx:play', { effect: 'splash', at: { ...r.water }, count: 14 })
  bus.emit('audio:cue', { cue: 'water_splash', at: r.water })
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

export function installFishing(ctx: GameContext): void {
  if (installed) return
  installed = true
  ctxRef = ctx

  /**
   * A TAP ON THE WATER IS A CAST. The town's pond is baked scenery with a
   * landmark hotspot on it, so this listener runs alongside the world crew's —
   * both walk the monster to the same bank, and this one is registered later so
   * its arrival callback is the one that survives. Water the KID built is a
   * real placeable and arrives on `play:interacted` instead, below.
   */
  bus.on('input:tap', (e) => {
    if (e.mode !== 'play') return
    if (!awake()) return

    // A PLACED THING ALWAYS WINS, fishing or not. Tapping the bench you are
    // standing next to must use the bench (LAW #1) rather than land your fish;
    // the bite window simply keeps running, and the fish comes back anyway.
    if (e.instanceId !== null) return

    // Already fishing? Then this tap is the catch (or a plop).
    if (rod !== null && (rod.state === 'waiting' || rod.state === 'biting')) {
      if (rod.state === 'biting') {
        // Claimed so the monster does not wander off mid-catch.
        keepStill()
        land(rod)
      } else if (e.ground !== null && isPondWater(e.ground.x, e.ground.z)) {
        plop(rod)
      }
      return
    }

    if (e.ground === null) return
    if (!isPondWater(e.ground.x, e.ground.z)) return
    // Aim at the bit of water the kid actually tapped, not at the middle.
    const aim = { x: e.ground.x, y: 0, z: e.ground.z }
    const water = pondCentre(aim)
    goThen(pondStand(aim), () => cast(water))
  })

  /** Water the kid painted themselves is a placeable, so it comes through here. */
  bus.on('play:interacted', (e) => {
    if (String(e.defId) !== WATER_DEF) return
    if (!awake()) return
    const c = instanceCentre(e.sceneId, e.instanceId)
    if (c === null) return
    cast({ x: c.x, y: 0.06, z: c.z })
  })

  // Anything that means "we are not fishing any more".
  bus.on('scene:left', () => reelIn())
  bus.on('app:modeChanged', (e) => {
    if (e.mode === 'build') reelIn()
  })
  bus.on('save:loaded', () => reelIn())

  engine.addSystem({
    id: 'catching.fishing',
    order: 48,
    fixed: (dt) => {
      const r = rod
      if (r === null) return
      if (!awake()) {
        reelIn()
        return
      }
      r.t += dt

      // Walked away: reel in quietly. Never a scold, never a lost fish.
      const p = app.playerPos
      if (Math.hypot(p.x - r.water.x, p.z - r.water.z) > LEASH) {
        reelIn()
        return
      }

      r.left -= dt
      if (r.state === 'waiting' && r.left <= 0) startBite(r)
      else if (r.state === 'biting' && r.left <= 0) fishSwamOff(r)
      else if (r.state === 'landing' && r.left <= 0) {
        const entry = r.hooked
        r.leap?.removeFromParent()
        r.leap = null
        r.hooked = null
        if (entry !== null) claimCatch(entry, { x: p.x, y: 0.9, z: p.z })
        // Straight back out. See the header: this is the loop.
        r.state = 'waiting'
        r.left = WAIT_MIN_S + engine.rng() * WAIT_SPAN_S
      }
    },
    render: () => {
      const r = rod
      if (r === null || ctxRef === null) return
      const bob = r.bob
      const ring = r.ring
      if (r.state === 'biting') {
        /**
         * THE DIP — AND THE FLOAT STAYS ON SCREEN WHILE IT HAPPENS.
         *
         * The first version ducked the bob to -0.42 with a 0.22 swing, which put
         * the whole two-tone ball under the surface for the entire bite: the
         * captured frame showed a ripple ring on empty water and nothing else,
         * and round `vr1` read it exactly that way. A dip you cannot see is not a
         * signal. It now plunges to about half the ball and springs back with a
         * hard lurch, rocking as it goes, so the thing a kid is watching is
         * MOVING rather than absent.
         */
        const k = Math.sin(r.t * 11)
        if (bob !== null) {
          bob.position.y = -0.14 + k * 0.2
          bob.rotation.z = Math.sin(r.t * 13) * 0.42
        }
        if (ring !== null) {
          // A HARDER RIPPLE. Round `vr2` wanted the dip to be "the most legible
          // thing on screen"; the ring is the cheapest half of that and it was
          // only doubling in size. It now more than triples.
          const s = 1 + 2.2 * (0.5 + 0.5 * Math.sin(r.t * 9))
          ring.scale.setScalar(s)
        }
        const shout = r.shout
        if (shout !== null) {
          const pulse = 0.85 + 0.28 * (0.5 + 0.5 * Math.sin(r.t * 9))
          shout.scale.setScalar(pulse)
          shout.rotation.z = Math.sin(r.t * 4) * 0.24
        }
      } else if (r.state === 'landing') {
        if (bob !== null) {
          bob.position.y = 0.6
          bob.rotation.z = 0
        }
        const leap = r.leap
        if (leap !== null) {
          const k = Math.max(0, Math.min(1, 1 - r.left / LAND_S))
          const arc = Math.sin(k * Math.PI)
          leap.position.set(
            r.water.x + (app.playerPos.x - r.water.x) * k,
            0.3 + arc * 2.1,
            r.water.z + (app.playerPos.z - r.water.z) * k,
          )
          leap.rotation.z = -arc * 1.1
          leap.rotation.y = k * 3
        }
      } else {
        if (bob !== null) {
          bob.position.y = Math.sin(r.t * 2.2) * 0.05
          bob.rotation.z = Math.sin(r.t * 1.3) * 0.06
        }
        if (ring !== null) ring.scale.setScalar(1 + Math.sin(r.t * 1.6) * 0.08)
      }
      /**
       * THE ROD FIRST, THEN THE LINE FROM ITS TIP. The line used to start at
       * `playerPos + 1.1`, which is a monster's midriff — so a kid saw a thread
       * appear out of their monster's tummy. It now leaves the tip of a rod the
       * monster is visibly holding, which is the whole difference between "a
       * line on the water" and "I am fishing".
       */
      tmpB.set(app.playerPos.x, 0, app.playerPos.z)
      tmpA.set(r.water.x, 0.25, r.water.z)
      aimRod(r.rod, tmpB, tmpA, tmpTip)
      aimLine(r.art, tmpA, tmpTip)
    },
  })
}

// ---------------------------------------------------------------------------
// test surface (read by tools/catch-check.mjs — never by the game)
// ---------------------------------------------------------------------------

export interface RodReport {
  state: FishState
  hooked: string | null
  left: number
  water: Vec3 | null
  /**
   * IS THE BITE ACTUALLY BEING SIGNALLED? Round `vr1` reported the bite as
   * unreadable — "a small teal blob and a flat white ellipse". The gold burst
   * and the half-submerged bob are the answer, and both are art, so both can
   * regress silently. These two numbers are what a gate can assert about them.
   */
  shout: boolean
  /** Where the float's ball is. Below about -0.36 and it is out of sight. */
  bobY: number
  /**
   * WHERE THE ROD TIP AND THE BURST ACTUALLY ARE, in world space.
   *
   * Round `vr2` reported two things a boolean cannot see: "there is no rod in
   * its hands, so the line reads as a stray wire" (the rod was there — it was
   * drawn across the monster's face) and "the bite is a small yellow starburst
   * drawn a long way from the float". Both are questions about a DISTANCE, so
   * the gate needs the two positions rather than a yes/no. Added, never
   * removed; `null` when the rod is in.
   */
  tip: Vec3 | null
  shoutAt: Vec3 | null
}

const reportTip = new THREE.Vector3()
const reportShout = new THREE.Vector3()

export function rodReport(): RodReport {
  if (rod === null)
    return {
      state: 'idle',
      hooked: null,
      left: 0,
      water: null,
      shout: false,
      bobY: 0,
      tip: null,
      shoutAt: null,
    }
  const tipNode = rod.rod.getObjectByName('tipAt')
  if (tipNode !== undefined) tipNode.getWorldPosition(reportTip)
  if (rod.shout !== null) rod.shout.getWorldPosition(reportShout)
  return {
    state: rod.state,
    hooked: rod.hooked?.id ?? null,
    left: Math.round(rod.left * 100) / 100,
    water: { ...rod.water },
    shout: rod.shout?.visible === true,
    bobY: Math.round((rod.bob?.position.y ?? 0) * 1000) / 1000,
    tip: tipNode === undefined ? null : { x: reportTip.x, y: reportTip.y, z: reportTip.z },
    shoutAt:
      rod.shout === null ? null : { x: reportShout.x, y: reportShout.y, z: reportShout.z },
  }
}

/** Cast without finding the pond. QA only; the game never calls this. */
export function forceCast(): boolean {
  if (!awake()) return false
  cast(pondCentre(app.playerPos))
  return rod !== null
}

/** Skip straight to the dip. QA only. */
export function forceBite(): string | null {
  if (rod === null) return null
  if (rod.state !== 'waiting') return null
  startBite(rod)
  return rod.hooked?.id ?? null
}

/** The tap that lands it. QA only — a kid does this with a finger. */
export function forceLand(): boolean {
  if (rod === null || rod.state !== 'biting') return false
  land(rod)
  return true
}

/** Let the bite window run out, on purpose, to prove nothing is lost. QA only. */
export function forceMiss(): boolean {
  if (rod === null || rod.state !== 'biting') return false
  fishSwamOff(rod)
  return true
}
